import { useState, useRef, useEffect } from "react";
import { ChevronDown, Building2, LogOut } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReduxState } from "@/redux/type";
import { clearProgram } from "@/redux/actions/programAction";
import { logout } from "@/redux/actions/sessionAction";
import { config } from "@/config";

export default function RewardsHeader() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const session = useSelector((state: ReduxState) => state.session);
    const program = useSelector((state: ReduxState) => state.program);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleSwitchProgram = () => {
        setIsDropdownOpen(false);
        dispatch(clearProgram());
        navigate("/select-program");
    };

    const handleLogout = () => {
        dispatch(logout());
        dispatch(clearProgram());
        localStorage.removeItem(config.localStorageKey);
        sessionStorage.clear();
        navigate("/login", { replace: true });
    };

    const getUserInitials = () => {
        if (session.email) return session.email.substring(0, 2).toUpperCase();
        return "U";
    };

    return (
        <header className="hidden h-16 items-center justify-between border-b border-gray-200 bg-white px-4 lg:flex lg:px-6">
            <div className="flex items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                    {program?.name || "Rewards Portal"}
                </h2>
            </div>

            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-3 rounded-lg px-3 py-2 transition-colors hover:bg-gray-50"
                >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600">
                        <span className="text-sm font-semibold text-white">{getUserInitials()}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {isDropdownOpen && (
                    <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
                        <div className="border-b border-gray-100 px-4 py-3">
                            <p className="truncate text-sm font-medium text-gray-900">{session.email}</p>
                        </div>
                        <div className="py-1">
                            <button
                                onClick={handleSwitchProgram}
                                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                <Building2 className="mr-3 h-4 w-4 text-gray-400" />
                                Switch Program
                            </button>
                        </div>
                        <div className="border-t border-gray-100 pt-1">
                            <button
                                onClick={handleLogout}
                                className="flex w-full items-center px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                            >
                                <LogOut className="mr-3 h-4 w-4" />
                                Logout
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
}
